"use client";

import { useEffect, useState } from "react";

/*
 * Bottom-right toast announcing the new site, styled 1:1 to the
 * outpacelabs/skills "glass" tokens: ink scale on white, chip surfaces,
 * rounded-full pills, and the shared EASE_OUT reveal (done with a plain
 * CSS transition so the landing page does not ship the motion runtime
 * just for this toast).
 *
 * Ink scale (from outpacelabs/skills globals.css):
 *   ink   rgba(23,23,23,0.92)  body rgba(23,23,23,0.62)
 *   muted rgba(23,23,23,0.42)  chip rgba(23,23,23,0.05)
 */
const STORAGE_KEY = "outpace-building-toast-dismissed";

function VerifiedBadge() {
	return (
		<svg
			viewBox="0 0 22 22"
			aria-hidden="true"
			width="15"
			height="15"
			xmlns="http://www.w3.org/2000/svg"
			className="block shrink-0"
		>
			<linearGradient
				gradientUnits="userSpaceOnUse"
				id="building-toast-verified-a"
				x1="4.411"
				x2="18.083"
				y1="2.495"
				y2="21.508"
			>
				<stop offset="0" stopColor="#f4e72a" />
				<stop offset=".539" stopColor="#cd8105" />
				<stop offset=".68" stopColor="#cb7b00" />
				<stop offset="1" stopColor="#f4ec26" />
				<stop offset="1" stopColor="#f4e72a" />
			</linearGradient>
			<linearGradient
				gradientUnits="userSpaceOnUse"
				id="building-toast-verified-b"
				x1="5.355"
				x2="16.361"
				y1="3.395"
				y2="19.133"
			>
				<stop offset="0" stopColor="#f9e87f" />
				<stop offset=".406" stopColor="#e2b719" />
				<stop offset=".989" stopColor="#e2b719" />
			</linearGradient>
			<g clipRule="evenodd" fillRule="evenodd">
				<path
					d="M13.324 3.848L11 1.6 8.676 3.848l-3.201-.453-.559 3.184L2.06 8.095 3.48 11l-1.42 2.904 2.856 1.516.559 3.184 3.201-.452L11 20.4l2.324-2.248 3.201.452.559-3.184 2.856-1.516L18.52 11l1.42-2.905-2.856-1.516-.559-3.184zm-7.09 7.575l3.428 3.428 5.683-6.206-1.347-1.247-4.4 4.795-2.072-2.072z"
					fill="url(#building-toast-verified-a)"
				/>
				<path
					d="M13.101 4.533L11 2.5 8.899 4.533l-2.895-.41-.505 2.88-2.583 1.37L4.2 11l-1.284 2.627 2.583 1.37.505 2.88 2.895-.41L11 19.5l2.101-2.033 2.895.41.505-2.88 2.583-1.37L17.8 11l1.284-2.627-2.583-1.37-.505-2.88zm-6.868 6.89l3.429 3.428 5.683-6.206-1.347-1.247-4.4 4.795-2.072-2.072z"
					fill="url(#building-toast-verified-b)"
				/>
				<path
					d="M6.233 11.423l3.429 3.428 5.65-6.17.038-.033-.005 1.398-5.683 6.206-3.429-3.429-.003-1.405.005.003z"
					fill="#d18800"
				/>
			</g>
		</svg>
	);
}

export default function BuildingToast() {
	const [show, setShow] = useState(false);
	const [visible, setVisible] = useState(false);
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		if (localStorage.getItem(STORAGE_KEY) === "1") return;
		const t = window.setTimeout(() => setMounted(true), 700);
		return () => window.clearTimeout(t);
	}, []);

	// Mount hidden first, then flip to visible on the next frame so the
	// CSS transition plays from the initial state.
	useEffect(() => {
		if (!mounted) return;
		setShow(true);
		const frame = requestAnimationFrame(() => setVisible(true));
		return () => cancelAnimationFrame(frame);
	}, [mounted]);

	function dismiss() {
		setVisible(false);
		try {
			localStorage.setItem(STORAGE_KEY, "1");
		} catch {
			// ignore storage failures
		}
	}

	function handleTransitionEnd() {
		if (!visible) setShow(false);
	}

	if (!show) return null;

	return (
		<aside
			aria-live="polite"
			onTransitionEnd={handleTransitionEnd}
			className={`fixed right-4 bottom-4 z-60 w-[min(320px,calc(100vw-2rem))] font-[var(--font-display)] transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none ${
				visible ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"
			}`}
		>
			<div className="relative rounded-[16px] border border-[rgba(23,23,23,0.08)] bg-white p-4 pr-9 shadow-[0_8px_30px_rgba(23,23,23,0.12)]">
				<button
					type="button"
					onClick={dismiss}
					aria-label="Dismiss"
					className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full text-[rgba(23,23,23,0.42)] transition hover:bg-[rgba(23,23,23,0.05)] hover:text-[rgba(23,23,23,0.92)] motion-safe:active:scale-[0.94]"
				>
					<svg
						aria-hidden="true"
						width="13"
						height="13"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
					>
						<path d="M18 6 6 18M6 6l12 12" />
					</svg>
				</button>

				<p className="text-[15px] font-[450] leading-[1.3] tracking-[-0.1px] text-[rgba(23,23,23,0.92)] text-balance">
					We&apos;re building a new website
				</p>
				<p className="mt-1.5 text-[13px] leading-[1.5] tracking-[0.1px] text-[rgba(23,23,23,0.62)] text-pretty">
					A fresh outpacestudios.com is on the way. Follow along on X for
					updates.
				</p>

				<a
					href="https://x.com/outpacestudios"
					target="_blank"
					rel="noopener noreferrer"
					className="mt-3 inline-flex h-9 items-center gap-1.5 rounded-full bg-[rgba(23,23,23,0.92)] pl-3 pr-4 text-[13px] font-[550] leading-none text-white transition hover:opacity-90 motion-safe:active:scale-[0.97]"
				>
					<VerifiedBadge />
					Follow on X
				</a>
			</div>
		</aside>
	);
}
